import { Alert, Badge, Button, Card, FileButton, Group, List, Modal, Progress, SimpleGrid, Stack, Tabs, Text, TextInput, Timeline } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconCalendarTime, IconCloudUpload, IconDatabaseExport, IconDatabaseImport, IconDownload, IconRestore, IconSettings, IconTrash, IconUpload,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { downloadFile, http } from "../../api/http";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, fmtNum, PageHeader, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

interface Backup { name: string; size_bytes: number; created_at: string; kind: "redovni" | "sigurnosna" | "uvezen"; ok: boolean }
interface RestoreStatus {
  file: string; user: string; status: "queued" | "running" | "done" | "failed"; started_at?: string; finished_at?: string;
  safety?: string; error?: string; steps: { t: string; msg: string }[];
}
interface Schedule {
  enabled: boolean; text: string; keep: number; next: string | null; last_auto: string | null;
  offsite: { enabled: boolean; keep: number; files: boolean; target: string | null;
    last: { status: "running" | "ok" | "failed"; file: string; target: string; started_at: string; finished_at?: string; error?: string;
      result?: { files?: { sent: number; unchanged: number; bytes: number } } } | null };
}
const KIND: Record<string, [string, string]> = {
  redovni: ["redovni", "gray"], sigurnosna: ["prije vraćanja", "orange"], uvezen: ["uvezen", "blue"],
};

function RestoreModal({ b, onClose }: { b: Backup; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState("");
  const go = useMutation({
    mutationFn: () => http.post(`/api/v1/system/backups/${encodeURIComponent(b.name)}/restore`, { confirm }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["restore-status"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={t("Vraćanje baze iz backup-a")} size="lg">
      <Stack>
        <Alert color="red" icon={<IconAlertTriangle />} title={t("Svi podaci u bazi biće zamijenjeni stanjem iz backup-a")}>
          <List size="sm" spacing={2}>
            <List.Item>{t("Prije vraćanja automatski se pravi sigurnosna kopija trenutnog stanja (prije_vracanja_…) — vraćanje se može poništiti vraćanjem te kopije.")}</List.Item>
            <List.Item>{t("Vraćanje ide u jednoj transakciji: ako bilo šta ne uspije, baza ostaje nepromijenjena.")}</List.Item>
            <List.Item>{t("Upisi napravljeni poslije vremena backup-a se gube. Korisnike obavijestite da ne rade dok traje vraćanje (nekoliko minuta).")}</List.Item>
            <List.Item>{t("Fajlovi (dokumenti, oblaci tačaka, ortofoto) nisu u backup-u baze — čuvaju se u skladištu i ostaju kakvi jesu.")}</List.Item>
          </List>
        </Alert>
        <Text size="sm">{t("Backup")}: <b>{b.name}</b> · {fmtDateTime(b.created_at)} · {fmtNum(b.size_bytes / 1e6, 1)} MB</Text>
        <TextInput label={t("Za potvrdu upišite tačan naziv fajla")} value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)}
          placeholder={b.name} data-no-translit="true" />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("Odustani")}</Button>
          <Button color="red" leftSection={<IconRestore size={16} />} disabled={confirm.trim() !== b.name} loading={go.isPending}
            onClick={() => go.mutate()}>{t("Vrati bazu")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default function BackupPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [restore, setRestore] = useState<Backup | null>(null);
  const backups = useQuery({ queryKey: ["backups"], refetchInterval: 10_000, queryFn: () => http.get<Backup[]>("/api/v1/system/backups") });
  const jobs = useQuery({ queryKey: ["jobs"], refetchInterval: 5_000, queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/system/jobs") });
  const st = useQuery({ queryKey: ["restore-status"], refetchInterval: (q) => (["queued", "running"].includes(q.state.data?.status ?? "") ? 3000 : 20_000),
    queryFn: () => http.get<RestoreStatus | null>("/api/v1/system/backups/restore-status") });
  const nav = useNavigate();
  const sch = useQuery({ queryKey: ["backup-schedule"], refetchInterval: 15_000, queryFn: () => http.get<Schedule>("/api/v1/system/backups/schedule") });
  const run = useMutation({ mutationFn: () => http.post<{ job_id: number }>("/api/v1/system/backups"),
    onSuccess: (r) => { notifications.show({ color: "green", message: `${t("Backup pokrenut, posao")} #${r.job_id}` }); qc.invalidateQueries({ queryKey: ["jobs"] }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });
  const upload = useMutation({
    mutationFn: (f: File) => { const fd = new FormData(); fd.append("file", f); return http.form<{ name: string }>("/api/v1/system/backups/upload", fd); },
    onSuccess: (r) => { notifications.show({ color: "green", message: `${t("Backup uvezen")}: ${r.name}` }); qc.invalidateQueries({ queryKey: ["backups"] }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const del = useMutation({
    mutationFn: (n: string) => http.del(`/api/v1/system/backups/${encodeURIComponent(n)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backups"] }),
  });
  const s = st.data;
  const busy = s && ["queued", "running"].includes(s.status);
  return (
    <AdminLayout>
      <Stack>
        <PageHeader title="Backup i vraćanje baze"
          description="Automatski backup baze po rasporedu iz podešavanja, kopija van servera (SFTP), ručni backup, uvoz backup fajla i vraćanje baze uz automatsku sigurnosnu kopiju (Pravilnik o standardima informacione bezbjednosti RS: kopije se čuvaju i van servera)."
          right={
            <Group gap="xs">
              <FileButton onChange={(f) => f && upload.mutate(f)} accept=".dump,.backup">
                {(p) => <Button {...p} variant="default" leftSection={<IconUpload size={16} />} loading={upload.isPending}>{t("Uvezi backup")}</Button>}
              </FileButton>
              <Button leftSection={<IconDatabaseExport size={16} />} onClick={() => run.mutate()} loading={run.isPending}>{t("Pokreni backup sada")}</Button>
            </Group>} />
        {sch.data && (() => {
          const d = sch.data; const o = d.offsite; const l = o.last;
          return (
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
              <Card withBorder radius="md">
                <Group gap={8} mb={4}><IconCalendarTime size={18} /><Text fw={650}>{t("Automatski backup")}</Text>
                  <Badge size="sm" variant="light" color={d.enabled ? "green" : "red"}>{t(d.enabled ? "uključen" : "isključen")}</Badge></Group>
                <Text size="sm">{t(d.text)}{d.enabled ? ` · ${t("čuva se")} ${d.keep} ${t("kopija")}` : ""}</Text>
                {d.next && <Text size="xs" c="dimmed">{t("Sljedeći")}: {fmtDateTime(d.next)}{d.last_auto ? ` · ${t("posljednji automatski")}: ${fmtDateTime(d.last_auto)}` : ""}</Text>}
              </Card>
              <Card withBorder radius="md">
                <Group gap={8} mb={4}><IconCloudUpload size={18} /><Text fw={650}>{t("Kopija van servera")}</Text>
                  {o.enabled
                    ? <Badge size="sm" variant="light" color={!l ? "gray" : l.status === "ok" ? "green" : l.status === "failed" ? "red" : "blue"}>
                        {t(!l ? "još nije poslata" : l.status === "ok" ? "uspješno" : l.status === "failed" ? "nije uspjela" : "u toku")}</Badge>
                    : <Badge size="sm" variant="light" color="orange">{t("nije podešena")}</Badge>}</Group>
                {o.enabled ? (
                  <>
                    <Text size="sm"><span data-no-translit="true">SFTP {o.target}</span> · {t("čuva se")} {o.keep} {t("kopija")}{o.files ? ` · ${t("i fajlovi iz skladišta")}` : ""}</Text>
                    {l && <Text size="xs" c="dimmed"><span data-no-translit="true">{l.file}</span> · {fmtDateTime(l.finished_at ?? l.started_at)}
                      {l.result?.files ? ` · ${t("fajlova poslato")}: ${l.result.files.sent}` : ""}</Text>}
                    {l?.error && <Text size="xs" c="red">{l.error}</Text>}
                  </>
                ) : <Text size="sm" c="dimmed">{t("Kopije su samo na ovom serveru. Ako server otkaže, backup se gubi zajedno s njim.")}</Text>}
              </Card>
            </SimpleGrid>
          );
        })()}
        <Group>
          <Button size="xs" variant="light" leftSection={<IconSettings size={14} />} onClick={() => nav("/admin/podesavanja?sekcija=backup")}>{t("Raspored i odredište backup-a")}</Button>
        </Group>
        {s && (
          <Alert color={s.status === "done" ? "green" : s.status === "failed" ? "red" : "blue"} icon={<IconDatabaseImport />}
            title={`${t("Vraćanje baze")}: ${s.file} — ${t(s.status === "done" ? "završeno" : s.status === "failed" ? "nije uspjelo" : "u toku")}`}>
            {busy && <Progress value={Math.min(95, 10 + s.steps.length * 15)} animated mb="xs" />}
            <Timeline bulletSize={12} lineWidth={2} active={s.steps.length - 1}>
              {s.steps.map((x, i) => <Timeline.Item key={i} title={<Text size="sm">{x.msg}</Text>}><Text size="xs" c="dimmed">{x.t}</Text></Timeline.Item>)}
            </Timeline>
            {s.error && <Text size="xs" c="red" mt="xs" ff="monospace" style={{ whiteSpace: "pre-wrap" }}>{s.error}</Text>}
            {s.status === "done" && <Text size="sm" mt="xs">{t("Pokrenuo")}: {s.user}. {t("Ako treba poništiti vraćanje, vratite sigurnosnu kopiju")} {s.safety}.</Text>}
          </Alert>
        )}
        <Tabs defaultValue="b">
          <Tabs.List><Tabs.Tab value="b">{t("Backup kopije")}</Tabs.Tab><Tabs.Tab value="j">{t("Red poslova")}</Tabs.Tab><Tabs.Tab value="u">{t("Uputstvo")}</Tabs.Tab></Tabs.List>
          <Tabs.Panel value="b" pt="md">
            <DataTable rows={(backups.data ?? []) as unknown as Record<string, unknown>[]} search={false} columns={[
              { key: "name", label: "Fajl", render: (r) => <Text size="sm" ff="monospace" data-no-translit="true">{String(r.name)}</Text> },
              { key: "kind", label: "Vrsta", render: (r) => { const k = KIND[String(r.kind)] ?? KIND.redovni; return <Badge size="sm" variant="light" color={k[1]}>{t(k[0])}</Badge>; } },
              { key: "size_bytes", label: "Veličina (MB)", render: (r) => r.ok ? fmtNum(Number(r.size_bytes) / 1e6, 1) : <Badge color="red" size="sm">{t("prazan — neuspio")}</Badge> },
              { key: "created_at", label: "Vrijeme", render: (r) => fmtDateTime(r.created_at) },
              { key: "d", label: "", render: (r) => (
                <Group gap={4} wrap="nowrap">
                  <Button size="compact-xs" variant="light" leftSection={<IconDownload size={12} />} disabled={!r.ok}
                    onClick={() => downloadFile(`/api/v1/system/backups/${r.name}`).catch((e) => notifications.show({ color: "red", message: e.message }))}>{t("Preuzmi")}</Button>
                  <Button size="compact-xs" variant="light" color="red" leftSection={<IconRestore size={12} />} disabled={!r.ok || !!busy}
                    onClick={() => setRestore(r as unknown as Backup)}>{t("Vrati")}</Button>
                  <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconTrash size={12} />}
                    onClick={() => del.mutate(String(r.name))}>{t("Obriši")}</Button>
                </Group>) },
            ]} />
          </Tabs.Panel>
          <Tabs.Panel value="j" pt="md">
            <DataTable rows={jobs.data ?? []} columns={[
              { key: "id", label: "#", type: "integer" }, { key: "task", label: "Posao" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
              { key: "progress", label: "Napredak", render: (r) => `${fmtNum(Number(r.progress) * 100, 0)} %` }, { key: "created_by", label: "Pokrenuo" },
              { key: "created_at", label: "Početak", render: (r) => fmtDateTime(r.created_at) }, { key: "finished_at", label: "Kraj", render: (r) => fmtDateTime(r.finished_at) },
              { key: "error", label: "Greška", render: (r) => <Text size="xs" c="red" lineClamp={2}>{String(r.error ?? "")}</Text> },
            ]} />
          </Tabs.Panel>
          <Tabs.Panel value="u" pt="md">
            <List size="sm" spacing={6} maw={900}>
              <List.Item><b>{t("Backup")}</b> — {t("automatski po rasporedu iz podešavanja (Podešavanja → Backup: učestalost, vrijeme, broj kopija); ručno dugmetom „Pokreni backup sada“ (npr. prije većih izmjena ili uvoza). Backup obuhvata cijelu bazu: registre, knjigu, dokumente (metapodatke), katastar, pravila GeoFence.")}</List.Item>
              <List.Item><b>{t("Čuvanje van servera")}</b> — {t("u Podešavanja → Backup izaberite SFTP odredište (NAS Agencije, drugi server); poslije svakog backup-a kopija se sama šalje tamo, a po želji i fajlovi iz skladišta. Bez toga: „Preuzmi“ i čuvajte kopiju na drugom mjestu.")}</List.Item>
              <List.Item><b>{t("Vraćanje")}</b> — {t("„Vrati“ kod izabrane kopije, upis naziva fajla za potvrdu. Sistem prvo pravi sigurnosnu kopiju (prije_vracanja_…), zatim vraća bazu u jednoj transakciji i usklađuje šemu sa verzijom aplikacije.")}</List.Item>
              <List.Item><b>{t("Vraćanje na drugi server")}</b> — {t("„Uvezi backup“ (fajl .dump sa starog servera), pa „Vrati“.")}</List.Item>
              <List.Item><b>{t("Fajlovi")}</b> — {t("dokumenti, fotografije, oblaci tačaka i ortofoto su u skladištu (volumen storage) — na SFTP odredište idu ako je uključeno „Kopirati i fajlove iz skladišta“ (prenose se samo novi i izmijenjeni).")}</List.Item>
            </List>
          </Tabs.Panel>
        </Tabs>
      </Stack>
      {restore && <RestoreModal b={restore} onClose={() => setRestore(null)} />}
    </AdminLayout>
  );
}
